<template>
  <div>
    <b-card class="rounded-lg">
      <b-card-header>
        <h4>{{ $t('accounting_income_transactions_create') }}</h4>
      </b-card-header>
      <b-card-body>
        <validation-observer ref="form">
          <b-form @submit.prevent="onSubmit">
            <b-row>
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Transaction Date"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('accounting_transactions_transaction_date')"
                    label-for="transaction_date"
                  >
                    <b-form-input
                      id="transaction_date"
                      v-model="form.transaction_date"
                      type="date"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Transaction Reason"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('accounting_transactions_transaction_reason')"
                    label-for="transaction_reason"
                  >
                    <v-select
                      id="transaction_reason"
                      v-model="form.transaction_reason"
                      :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                      :options="transactionReasonOptions"
                      :reduce="val => val.value"
                      :placeholder="$t('accounting_transactions_transaction_reason')"
                      :loading="loadingReasons"
                      :label="$i18n.locale == 'en' ? 'label' : 'label_ar'"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <receiver-chart-fields
                :value="form.fund_gl_account_id"
                :label="$t('accounting_paid_from')"
                input-id="fund_gl_account_id"
                :blocked-id="form.receiver_gl_account_id"
                :excluded-ids="excludedChartIds"
                :server-error="localizedFieldError('fund_gl_account_id')"
                @input="onFundChart"
              />
              <receiver-chart-fields
                :value="form.receiver_gl_account_id"
                :label="$t('accounting_received_by')"
                input-id="receiver_gl_account_id"
                :blocked-id="form.fund_gl_account_id"
                :excluded-ids="excludedChartIds"
                :server-error="receiverError"
                @input="onReceiverChart"
              />
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Amount"
                  rules="required|min_value:0.01"
                >
                  <b-form-group
                    :label="$t('accounting_transactions_amount')"
                    label-for="amount"
                  >
                    <b-form-input
                      id="amount"
                      v-model.number="form.amount"
                      type="number"
                      step="0.01"
                      :placeholder="$t('accounting_transactions_amount')"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12">
                <transaction-money-fields
                  direction="income"
                  :amount="form.amount"
                  :fee.sync="form.fee"
                  :file.sync="invoiceFile"
                  :balance="selectedBalance"
                />
              </b-col>
              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  :label="$t('accounting_transactions_category')"
                  label-for="category_id"
                >
                  <v-select
                    id="category_id"
                    v-model="form.category_id"
                    :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                    :options="categoryOptions"
                    :reduce="val => val.id"
                    :get-option-label="option => option.name"
                    :placeholder="$t('accounting_transactions_category')"
                    :loading="loadingCategories"
                    clearable
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  :label="$t('accounting_transactions_project')"
                  label-for="project_id"
                >
                  <v-select
                    id="project_id"
                    v-model="form.project_id"
                    :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                    :options="projectOptions"
                    :reduce="val => val.id"
                    :get-option-label="option => option.name || option.code"
                    :placeholder="$t('accounting_transactions_project')"
                    :loading="loadingProjects"
                    clearable
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  :label="$t('accounting_transactions_receipt_path')"
                  label-for="receipt_path"
                >
                  <b-form-input
                    id="receipt_path"
                    v-model="form.receipt_path"
                    :placeholder="$t('accounting_transactions_receipt_path')"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="12">
                <b-form-group
                  :label="$t('accounting_transactions_description')"
                  label-for="description"
                >
                  <b-form-textarea
                    id="description"
                    v-model="form.description"
                    rows="3"
                    :placeholder="$t('accounting_transactions_description')"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col
                cols="12"
                class="mt-2"
              >
                <b-button
                  type="submit"
                  variant="primary"
                  class="mr-1"
                  :disabled="submitting || feeBlocked"
                >
                  {{ submitting ? $t('accounting_transactions_saving') : $t('accounting_transactions_save') }}
                </b-button>
                <b-button
                  variant="secondary"
                  @click="$router.go(-1)"
                >
                  {{ $t('accounting_transactions_cancel_btn') }}
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BRow,
  BCol,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, min_value } from '@validations'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as accountsAPI from '@/api/accounting/accounts'
import * as transactionReasonsAPI from '@/api/accounting/transactionReasons'
import { mapGetters } from 'vuex'
import { feeExceedsAmount, withInvoiceFile } from '@/utils/transactionMoney'
import {
  drawerChartIds, fetchActivePayouts, fieldError, payoutOptionLabel, voucherErrorMessage, walletOptionLabel,
} from '@/utils/voucherSource'
import * as walletsAPI from '@/api/accounting/wallets'
import TransactionMoneyFields from './TransactionMoneyFields.vue'
import ReceiverChartFields from './ReceiverChartFields.vue'

const FUND_ERROR_KEYS = {
  'Cash cannot be received from the same account it is deposited into.': 'accounting_fund_same_account',
  'Chart account is inactive or does not accept posting.': 'accounting_fund_gl_inactive',
  'This chart account belongs to a cash, bank, or wallet account. Choose that account instead.': 'accounting_fund_gl_till',
  'Receiving chart account is inactive or does not accept posting.': 'accounting_receiver_inactive',
}

function localToday() {
  const date = new Date()
  const pad = value => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BRow,
    BCol,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    ToastificationContent,
    TransactionMoneyFields,
    ReceiverChartFields,
  },
  computed: {
    ...mapGetters({
      projects: 'projects/projects',
    }),
    paymentSourceLabel() {
      if (this.form.payment_source === 'wallet') return this.$t('accounting_transactions_custody_wallet')
      if (this.form.payment_source === 'employee_payment_account') return this.$t('accounting_transactions_employee_payment_account')
      if (this.form.payment_source === 'account') return this.$t('accounting_transactions_company_account')
      return this.$t('accounting_transactions_source')
    },
    paymentSourceOptions() {
      return [
        { value: 'account', label: this.$t('accounting_transactions_company_account') },
        { value: 'wallet', label: this.$t('accounting_transactions_custody_wallet') },
        { value: 'employee_payment_account', label: this.$t('accounting_transactions_employee_payment_account') },
      ]
    },
    sourceOptions() {
      if (this.form.payment_source === 'wallet') return this.wallets
      if (this.form.payment_source === 'account') return this.accounts
      if (this.form.payment_source === 'employee_payment_account') return this.payoutAccounts
      return []
    },
    selectedSource() {
      return (this.sourceOptions || []).find(item => item.id === this.form.source_id) || null
    },
    selectedBalance() {
      if (this.form.payment_source === 'employee_payment_account') return null
      return this.selectedSource ? this.selectedSource.current_balance : null
    },
    feeBlocked() {
      return feeExceedsAmount('income', this.form.amount, this.form.fee)
    },
    excludedChartIds() {
      return drawerChartIds([...(this.accounts || []), ...(this.wallets || []), ...(this.payoutAccounts || [])])
    },
    sourceLoading() {
      if (this.form.payment_source === 'wallet') return this.loadingWallets
      if (this.form.payment_source === 'account') return this.loadingAccounts
      if (this.form.payment_source === 'employee_payment_account') return this.loadingPayouts
      return false
    },
  },
  data() {
    return {
      submitting: false,
      loadingAccounts: false,
      loadingWallets: false,
      loadingPayouts: false,
      loadingReasons: false,
      loadingCategories: false,
      loadingProjects: false,
      accounts: [],
      wallets: [],
      payoutAccounts: [],
      sourceError: '',
      transactionReasonOptions: [],
      categoryOptions: [],
      projectOptions: [],
      receiverError: '',
      fieldErrors: {},
      invoiceFile: null,
      required,
      min_value,
      form: {
        transaction_reason: null,
        amount: null,
        fee: 0,
        category_id: null,
        project_id: null,
        fund_gl_account_id: null,
        receiver_gl_account_id: null,
        description: null,
        payment_source: null,
        source_id: null,
        receipt_path: null,
        transaction_date: localToday(),
      },
    }
  },
  async mounted() {
    await Promise.all([
      this.loadAccounts(),
      this.loadWallets(),
      this.loadPayouts(),
      this.loadTransactionReasons(),
      this.loadCategories(),
      this.loadProjects(),
    ])
  },
  methods: {
    async onSubmit() {
      this.sourceError = ''
      this.receiverError = ''
      const isValid = await this.$refs.form.validate()
      if (!isValid || this.feeBlocked || !this.form.fund_gl_account_id || !this.form.receiver_gl_account_id) return

      this.submitting = true
      try {
        await this.$store.dispatch('accounting/incomeTransactions/create', withInvoiceFile(
          this.incomePayload(),
          this.invoiceFile,
        ))
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transactions_messages.created_success'),
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.$router.push({ name: 'accounting-income-transactions-list' })
      } catch (error) {
        this.sourceError = fieldError(error, 'source_id')
        this.fieldErrors = {
          fund_gl_account_id: fieldError(error, 'fund_gl_account_id'),
          receiver_gl_account_id: fieldError(error, 'receiver_gl_account_id'),
        }
        this.receiverError = this.localizedFieldError('receiver_gl_account_id')
        const errorMessage = voucherErrorMessage(error, this.$t('accounting_transactions_messages.error'))
        this.$toast({
          component: ToastificationContent,
          props: {
            title: errorMessage,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.submitting = false
      }
    },
    onPaymentSourceChange() {
      this.form.source_id = null
      this.sourceError = ''
    },
    incomePayload() {
      const body = {
        transaction_reason: this.form.transaction_reason,
        amount: this.form.amount,
        fee: this.form.fee,
        category_id: this.form.category_id || null,
        project_id: this.form.project_id || null,
        description: this.form.description || null,
        receipt_path: this.form.receipt_path || null,
        transaction_date: this.form.transaction_date,
        fund_gl_account_id: this.form.fund_gl_account_id,
        receiver_gl_account_id: this.form.receiver_gl_account_id,
      }
      return body
    },
    localizedFieldError(field) {
      const raw = this.fieldErrors[field] || ''
      if (!raw) return ''
      const key = FUND_ERROR_KEYS[raw]
      return key ? this.$t(key) : raw
    },
    onFundChart(id) {
      this.form.fund_gl_account_id = id
      if (this.fieldErrors) this.fieldErrors.fund_gl_account_id = ''
    },
    onReceiverChart(id) {
      this.form.receiver_gl_account_id = id
      this.receiverError = ''
    },
    getSourceOptionLabel(option) {
      if (!option) return ''
      if (this.form.payment_source === 'wallet') return walletOptionLabel(option)
      if (this.form.payment_source === 'employee_payment_account') return payoutOptionLabel(option)
      return `${option.code} - ${option.name}`
    },
    async loadPayouts() {
      this.loadingPayouts = true
      try {
        this.payoutAccounts = await fetchActivePayouts()
      } catch (error) {
        console.error('Error loading payout accounts:', error)
        this.payoutAccounts = []
      } finally {
        this.loadingPayouts = false
      }
    },
    async loadWallets() {
      this.loadingWallets = true
      try {
        const response = await walletsAPI.getWallets({ status: 'active', per_page: 1000 })
        this.wallets = response.data || []
      } catch (error) {
        console.error('Error loading wallets:', error)
      } finally {
        this.loadingWallets = false
      }
    },
    async loadAccounts() {
      this.loadingAccounts = true
      try {
        const response = await accountsAPI.getAccounts({ is_active: true, per_page: 1000 })
        this.accounts = response.data || []
      } catch (error) {
        console.error('Error loading accounts:', error)
      } finally {
        this.loadingAccounts = false
      }
    },
    async loadTransactionReasons() {
      this.loadingReasons = true
      try {
        const response = this.$i18n.locale === 'ar'
          ? await transactionReasonsAPI.getIncomeTransactionReasonOptionsAr()
          : await transactionReasonsAPI.getIncomeTransactionReasonOptions()

        // Debug: Log the response to see its structure
        console.log('Transaction Reasons API Response:', response)

        // Handle different response structures
        // The request interceptor returns response.data, so response is already unwrapped
        let reasons = []
        if (Array.isArray(response)) {
          // Response is directly an array
          reasons = response
        } else if (response && response.success && Array.isArray(response.data)) {
          // Response has { success: true, data: [...] }
          reasons = response.data
        } else if (response && Array.isArray(response.data)) {
          // Response has { data: [...] }
          reasons = response.data
        } else if (response && response.data && Array.isArray(response.data.data)) {
          // Nested response structure
          reasons = response.data.data
        } else {
          console.warn('Unexpected response structure:', response)
        }

        console.log('Parsed transaction reasons:', reasons)
        this.transactionReasonOptions = reasons || []
      } catch (error) {
        console.error('Error loading transaction reasons:', error)
        if (error.response) {
          console.error('Error response:', error.response.data)
        }
        // Fallback to empty array
        this.transactionReasonOptions = []
      } finally {
        this.loadingReasons = false
      }
    },
    async loadCategories() {
      this.loadingCategories = true
      this.categoryOptions = []
      this.loadingCategories = false
    },
    async loadProjects() {
      this.loadingProjects = true
      try {
        // Load projects from store if available
        if (this.projects && this.projects.length > 0) {
          this.projectOptions = this.projects
        } else {
          // TODO: Load projects from API if needed
          this.projectOptions = []
        }
      } catch (error) {
        console.error('Error loading projects:', error)
        this.projectOptions = []
      } finally {
        this.loadingProjects = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
